import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SidebarProvider } from './context/SidebarContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import MainLayout from './components/MainLayout';
import ConditionalLayout from './components/ConditionalLayout';
import LandingPage from './pages/LandingPage';
import PremiumLandingPage from './pages/PremiumLandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AuthSuccessPage from './pages/AuthSuccessPage';
import OnboardingWelcome from './pages/OnboardingWelcome';
import OnboardingProfile from './pages/OnboardingProfile';
import OnboardingFirstSkill from './pages/OnboardingFirstSkill';
import BrowsePage from './pages/BrowsePage';
import CategoryPage from './pages/CategoryPage';
import SkillDetailPage from './pages/SkillDetailPage';
import EditSkillPage from './pages/EditSkillPage';
import SkillBuilderPage from './pages/SkillBuilderPage';
import Step1PurposeEnhanced from './pages/builder/Step1PurposeEnhanced';
import Step2Generate from './pages/builder/Step2Generate';
import Step3Publish from './pages/builder/Step3Publish';
import Step4Download from './pages/builder/Step4Download';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ProfileEditPage from './pages/ProfileEditPage';
import DashboardPage from './pages/DashboardPage';
import MySkillsPage from './pages/MySkillsPage';
import StarredPage from './pages/StarredPage';
import SkillIdeaAssistantPage from './pages/SkillIdeaAssistantPage';
import './index.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7EFE7]">
        <div data-testid="loading-spinner" className="spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/" />;
};

const AppRoutes = () => {
  return (
    <div className="min-h-screen bg-white">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PremiumLandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/success" element={<AuthSuccessPage />} />
        <Route path="/browse" element={<ConditionalLayout><BrowsePage /></ConditionalLayout>} />
        <Route path="/categories/:slug" element={<ConditionalLayout><CategoryPage /></ConditionalLayout>} />
        <Route path="/skills/:id" element={<ConditionalLayout><SkillDetailPage /></ConditionalLayout>} />
        <Route path="/skills/:id/edit" element={<ProtectedRoute><ConditionalLayout><EditSkillPage /></ConditionalLayout></ProtectedRoute>} />
        
        {/* Onboarding Routes - Protected */}
        <Route
          path="/onboarding/welcome"
          element={
            <ProtectedRoute>
              <OnboardingWelcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/profile"
          element={
            <ProtectedRoute>
              <OnboardingProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/first-skill"
          element={
            <ProtectedRoute>
              <OnboardingFirstSkill />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes - With Sidebar Layout */}
        <Route
          path="/profile/:id"
          element={
            <ProtectedRoute>
              <MainLayout><ProfilePage /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/builder/:id/step-1"
          element={
            <ProtectedRoute>
              <MainLayout><Step1PurposeEnhanced /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/builder/:id/step-2"
          element={
            <ProtectedRoute>
              <MainLayout><Step2Generate /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/builder/:id/step-3"
          element={
            <ProtectedRoute>
              <MainLayout><Step3Publish /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/builder/:id/step-4"
          element={
            <ProtectedRoute>
              <MainLayout><Step4Download /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/builder"
          element={
            <ProtectedRoute>
              <MainLayout><Step1PurposeEnhanced /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/builder/new"
          element={
            <ProtectedRoute>
              <MainLayout><Step1PurposeEnhanced /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <MainLayout><ProfileEditPage /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout><DashboardPage /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/discover"
          element={
            <ProtectedRoute>
              <MainLayout><SkillIdeaAssistantPage /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-skills"
          element={
            <ProtectedRoute>
              <MainLayout><MySkillsPage /></MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/starred"
          element={
            <ProtectedRoute>
              <MainLayout><StarredPage /></MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SidebarProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </SidebarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;